import {type FormEvent, use, useCallback, useMemo, useId, useRef, useState} from 'react'
import {Button, FormControl, IconButton, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {CheckIcon, LockIcon, ThreeBarsIcon, UnlockIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {isStaff} from '@github-ui/stats'
import type {Session} from '../../types/session'
import type {Pull} from '../../types/pull'
import type {HydratedAPITask} from '../../types/api-task'
import {useCurrentUser} from '../../contexts/CurrentUserContext'
import {OpenInMenuV2} from './OpenInMenuV2'
import {PullRequestButton} from './PullRequestButton'
import {StickyTaskChatHeaderMenu} from './StickyTaskChatHeaderMenu'
import {
  hasStaffSessionDetails,
  StickyHeaderSessionDetails,
  StickyHeaderStaffSessionDetails,
} from './StickyTaskChatHeaderSessionDetails'
import {useTaskActionItems} from '../../hooks/use-task-action-items'
import {TaskPinErrorDialog} from '../TaskPinErrorDialog'
import {isQuickChatTask} from '../../utils/is-quick-chat-task'
import {getTaskTitle} from '../../utils/task-helpers'
import {canChangeTaskSharing} from '../../utils/task-sharing'
import {resolveEffectiveBranchName} from '../../utils/task-artifact-helpers'
import {useAgentProfile} from '../../contexts/agent-profile-context'
import {
  getLeadingVisualIconAttributes,
  getLeadingVisualStatusLabel,
} from '../../utils/get-leading-visual-icon-attributes'
import {sumSessionSpendNanoAiu} from '../../utils/session-usage'
import {Icon} from '@github-ui/pacer/Icon'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useTaskSharingDialogOptional} from '../../contexts/TaskSharingDialogContext'
import {useRenameTask} from '../../hooks/use-rename-task'
import {useIsAhpSession} from '../../hooks/use-is-ahp-session'
import {useRelayHostInfo} from '../../ahp/use-relay-host-info'
import {useRelaySessionUsage} from '../../ahp/use-relay-session-usage'
import {
  ENVIRONMENT_KIND_ICONS,
  resolveEnvironmentStatus,
} from '@github-ui/copilot-shared-ui/environments/environment-display'
import {setTaskTitleOverride} from '@github-ui/copilot-shared-ui/hooks/use-task-title-override'
import {ConversationExportMenuItem} from '@github-ui/copilot-shared-ui/components/ConversationActionsMenu'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import {useSessionAgentResolution} from './use-session-agent-resolution'
import {fromAhpChatState} from '@github-ui/copilot-conversation-export/ahp-adapter'
import {downloadConversation} from '@github-ui/copilot-conversation-export/download'
import type {ExportFormat} from '@github-ui/copilot-conversation-export/types'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {LayoutContext} from '@github-ui/pacer/Layout/layout-context'
import {SidebarPageLayout, useIsInsideSidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {TaskDebugLogButton} from './TaskDebugLogButton'
import {TimelineVerbosityControl} from './TimelineVerbosityControl'
import {AgentsWorkbenchToggle} from '../workbench/AgentsWorkbenchToggle'
import {getEnvironmentFreshness, useAnnounceEnvironmentDetails} from '../../hooks/use-environment-details-visibility'
import styles from './StickyTaskChatHeader.module.css'

// Keep a pointer-down on the sidebar toggle from reaching Pacer's document-level
// "click outside" listener, which would otherwise close the floating sidebar just
// before the button's click reopens it.
function stopPointerPropagation(event: {stopPropagation: () => void}) {
  event.stopPropagation()
}

interface StickyTaskChatHeaderProps {
  sessions: Session[]
  pull?: Pull
  task?: HydratedAPITask
  repoOwner?: string
  repoName?: string
  branchName?: string
  hideArchive?: boolean
  hideActions?: boolean
  hideEnvironmentVisual?: boolean
  isCodeReviewMode?: boolean
  dataUpdatedAt?: number
  onUserRename?: (title: string) => void
  chatApiUrl?: string
  showVerbosityControl?: boolean
}

export function StickyTaskChatHeader({
  sessions,
  pull,
  task,
  repoOwner,
  repoName,
  branchName,
  hideArchive = false,
  hideActions = false,
  hideEnvironmentVisual = false,
  isCodeReviewMode = false,
  dataUpdatedAt,
  onUserRename,
  chatApiUrl,
  showVerbosityControl = false,
}: StickyTaskChatHeaderProps) {
  const [isRenaming, setIsRenaming] = useState(false)
  const {state: establishedAhpSessionState, sessionState: establishedAhpSessionMetadata} =
    useOptionalEstablishedAhpSessionViewState()
  const titleButtonRef = useRef<HTMLButtonElement>(null)
  const shareButtonRef = useRef<HTMLButtonElement>(null)
  const [{isNarrow}, headerMeasureRef] = useContainerIsNarrow()
  // Optional: only present in the legacy task route's Pacer Layout. Absent in
  // the repo-less SidebarPageLayout route, side panels, and Storybook.
  const layoutContext = use(LayoutContext)
  const isInsideSidebarPageLayout = useIsInsideSidebarPageLayout()

  const mostRecentSession = sessions[sessions.length - 1]
  const [detailsVisible, setDetailsVisible] = useState(false)
  const {
    environment,
    reachable,
    isAhpSession,
    connectionProgress,
    relayStatus,
    loading: environmentLoading,
    error: environmentError,
    refetchEnvironment,
  } = useIsAhpSession(mostRecentSession, {observeEnvironment: detailsVisible})
  const agentResolution = useSessionAgentResolution({
    session: mostRecentSession,
    isAhpSession,
    detailsVisible,
    liveChatState: establishedAhpSessionState,
    liveSessionState: establishedAhpSessionMetadata,
  })
  const environmentFreshness = getEnvironmentFreshness({
    hasEnvironment: !!environment,
    loading: environmentLoading,
    error: !!environmentError,
  })
  const environmentStatus = environment
    ? resolveEnvironmentStatus(environment, reachable, connectionProgress, relayStatus).label
    : undefined
  const environmentAnnouncement = [
    environmentStatus && `Environment status: ${environmentStatus}`,
    environmentFreshness,
  ]
    .filter(Boolean)
    .join('. ')
  useAnnounceEnvironmentDetails(detailsVisible, environmentAnnouncement || undefined)
  const handleToggleDetails = useCallback(
    (visible: boolean) => {
      setDetailsVisible(visible)
      if (visible) void refetchEnvironment?.()
    },
    [refetchEnvironment],
  )
  const hostInfo = useRelayHostInfo(mostRecentSession?.task_id)
  const sessionUsage = useRelaySessionUsage(mostRecentSession?.task_id)
  const liveSpendNanoAiu = reachable ? sessionUsage?.tokens?.nanoAiu : undefined
  const persistedSpendNanoAiu = sumSessionSpendNanoAiu(sessions)
  const spendNanoAiu = liveSpendNanoAiu ?? persistedSpendNanoAiu
  const firstSession = sessions[0]
  const {currentUser} = useCurrentUser()
  const currentUserIsOwner = !!currentUser?.id && currentUser.id === task?.creator?.id
  const agentProfile = useAgentProfile()
  const TrailingVisual = agentProfile.slots.TrailingVisual
  const isCLI = agentProfile.interactiveSession
  const sharingDialog = useTaskSharingDialogOptional()

  const effectiveBranchName = resolveEffectiveBranchName(branchName, task, pull)

  const handleStartRename = () => {
    setIsRenaming(true)
    sendEvent('agent_sessions.task_actions', {
      target: 'RENAME_TASK_START',
      task_id: task?.id,
    })
  }

  const actionProps = {
    enablePinning: !hideActions,
    state: mostRecentSession?.state,
    taskId: task?.id,
    repoOwner,
    repoName,
    currentUserIsOwner,
    archivedAt: task?.archived_at,
    eventType: firstSession?.event_type,
    eventIdentifiers: firstSession?.event_identifiers,
    pull,
    hideCopy: agentProfile.privateSessions,
    hideArchive,
    interactiveSession: isCLI,
    agentSlug: agentProfile.slug,
    isQuickChatTask: isQuickChatTask(task),
    hideShare: !isNarrow || !agentProfile.privateSessions,
    sharingStatus: task?.sharing_status,
    hidePull: isCodeReviewMode,
    automationId: task?.automation_id,
    onRename: handleStartRename,
    // At narrow widths the sharing action lives in the title menu, so return
    // focus to the title trigger when the sharing dialog closes.
    shareReturnFocusRef: titleButtonRef,
  }
  const {items: actionItems, pinError, dismissPinError} = useTaskActionItems(actionProps)

  // Match the session row's state-based leading visual.
  const iconAttributes = useMemo(
    () =>
      getLeadingVisualIconAttributes(
        pull?.state,
        pull?.inMergeQueue,
        mostRecentSession?.state,
        !!task?.archived_at,
        mostRecentSession?.agent_slug,
      ),
    [pull?.state, pull?.inMergeQueue, mostRecentSession?.state, mostRecentSession?.agent_slug, task?.archived_at],
  )
  const showStatusIcon = iconAttributes.icon !== CheckIcon

  const title = getTaskTitle(task, pull?.title, {
    preferPullTitle: isCodeReviewMode,
    formatAsReview: isCodeReviewMode,
  })
  const handleExport = useCallback(
    async (format: ExportFormat) => {
      if (!establishedAhpSessionState) return
      try {
        await downloadConversation(
          fromAhpChatState(establishedAhpSessionState, {
            title,
            user: getCurrentUserLogin() || undefined,
          }),
          format,
        )
      } catch {
        announce('The conversation could not be exported.')
      }
    },
    [establishedAhpSessionState, title],
  )

  const statusLabel = useMemo(
    () => getLeadingVisualStatusLabel(pull?.state, pull?.inMergeQueue, mostRecentSession?.state, !!task?.archived_at),
    [pull?.state, pull?.inMergeQueue, mostRecentSession?.state, task?.archived_at],
  )

  const leadingVisual = showStatusIcon ? (
    <span data-testid={iconAttributes['data-testid']}>
      <Icon icon={iconAttributes.icon} color={iconAttributes.color} className={styles.statusIcon} />
    </span>
  ) : null

  // Our AHP environment kind is the more precise signal than the agent-profile
  // trailing visual, which is feature-flag gated and describes the agent type.
  const EnvironmentIcon = environment ? ENVIRONMENT_KIND_ICONS[environment.kind] : undefined
  const environmentVisual = hideEnvironmentVisual ? undefined : EnvironmentIcon ? (
    <EnvironmentIcon size={16} />
  ) : TrailingVisual ? (
    <TrailingVisual
      state={mostRecentSession?.state}
      staleAt={task?.stale_at}
      dataUpdatedAt={dataUpdatedAt}
      sharingStatus={task?.sharing_status}
    />
  ) : undefined

  // `hostInfo` describes the creator's machine (host name, filesystem path, and
  // how many sessions they are running). A shared task is viewable by
  // collaborators, so keep those rows out of everyone else's popover.
  const ownerHostInfo = currentUserIsOwner ? hostInfo : undefined
  const repository = repoOwner && repoName ? `${repoOwner}/${repoName}` : pull?.repository_nwo
  // The Task API returns `owner: {id: 0}` when the owner is the current user
  // themselves, with no useful login. Fall back to the task creator so managed
  // environments still have something to render. Sandbox chats scoped to an
  // org/user surface that scope as the owner instead.
  const taskOwnerLogin = task?.compute?.scope ?? (task?.owner?.id ? task.owner.login : task?.creator?.login)
  const fallbackEnvironmentKind = isAhpSession ? task?.current_environment?.kind : undefined

  const sessionDetails = (
    <StickyHeaderSessionDetails
      session={mostRecentSession}
      agentResolution={agentResolution}
      isAhpSession={isAhpSession}
      task={task}
      currentUserIsOwner={currentUserIsOwner}
      environment={environment}
      hostInfo={ownerHostInfo}
      usage={sessionUsage}
      spendNanoAiu={spendNanoAiu}
      relayReachable={reachable}
      connectionProgress={connectionProgress}
      relayStatus={relayStatus}
      isMultiSession={sessions.length > 1}
      branchName={effectiveBranchName}
      owner={taskOwnerLogin}
      ownerLogin={taskOwnerLogin}
      repository={repository}
      fallbackEnvironmentKind={fallbackEnvironmentKind}
      dataUpdatedAt={dataUpdatedAt}
      environmentLoading={environmentLoading}
      environmentError={environmentError}
    />
  )

  const staffDetails =
    isStaff() && hasStaffSessionDetails({environment, hostInfo}) ? (
      <StickyHeaderStaffSessionDetails environment={environment} hostInfo={hostInfo} />
    ) : undefined

  const isShared = task?.sharing_status === 'shared'
  const showShareButton =
    sharingDialog &&
    canChangeTaskSharing({
      taskId: task?.id,
      currentUserIsOwner,
      hasRepository: Boolean(repoOwner && repoName),
      supportsSharing: agentProfile.privateSessions,
    })

  const handleShareClick = () => {
    if (!task?.id || !sharingDialog) return
    sharingDialog.openSharingDialog(task.id, repoOwner, repoName, isShared, shareButtonRef)
  }

  const {mutateAsync: renameMutateAsync} = useRenameTask({
    owner: repoOwner,
    repo: repoName,
    taskId: task?.id,
    apiUrl: chatApiUrl,
  })

  // Throws on failure so the dialog stays open and surfaces the error; only
  // closes the dialog on a successful rename.
  const handleRenameSubmit = async (newName: string) => {
    await renameMutateAsync({name: newName})
    if (task?.id) setTaskTitleOverride(task.id, newName, task.name ?? '')
    onUserRename?.(newName)
    sendEvent('agent_sessions.task_actions', {target: 'RENAME_TASK_SAVE', task_id: task?.id})
    setIsRenaming(false)
  }

  return (
    <div className={styles.headerWrapper}>
      <TaskPinErrorDialog error={pinError} onClose={dismissPinError} returnFocusRef={titleButtonRef} />
      {isRenaming && (
        <RenameTaskDialog
          currentName={task?.name ?? 'New task'}
          onCancel={() => setIsRenaming(false)}
          onSubmit={handleRenameSubmit}
          returnFocusRef={titleButtonRef}
        />
      )}
      <div className={styles.header} ref={headerMeasureRef} data-testid="sticky-task-chat-header">
        {/* The repo-less task route uses SidebarPageLayout; its trigger belongs
            in this header so it stays aligned with the title. The legacy task
            route still uses Pacer's toggle at its layout breakpoint. */}
        {isInsideSidebarPageLayout ? (
          <SidebarPageLayout.SidebarTrigger className={styles.sidebarToggle} />
        ) : (
          layoutContext?.layoutBreakpoint && (
            <IconButton
              icon={ThreeBarsIcon}
              aria-label="Toggle sidebar"
              variant="invisible"
              onClick={layoutContext.toggleSidebar}
              onMouseDown={stopPointerPropagation}
              onTouchStart={stopPointerPropagation}
              className={styles.sidebarToggle}
              data-testid="sticky-header-sidebar-toggle"
            />
          )
        )}
        {/* Title and environment details share one popover trigger. A visually hidden h1
            preserves the page's primary heading (parity with the non-sticky
            TaskChatHeader, which rendered the title as PageHeader.Title as="h1"). */}
        <div className={styles.titleArea}>
          <h1 className="sr-only">{title}</h1>
          <StickyTaskChatHeaderMenu
            title={title}
            triggerVisual={environmentVisual}
            leadingVisual={leadingVisual}
            actionItems={hideActions ? [] : actionItems}
            actionItemAfterRename={
              hideActions ? undefined : (
                <ConversationExportMenuItem onExport={establishedAhpSessionState ? handleExport : undefined} />
              )
            }
            anchorRef={titleButtonRef}
            actionError={pinError}
            staffDetails={staffDetails}
            onToggleDetails={handleToggleDetails}
          >
            {sessionDetails}
          </StickyTaskChatHeaderMenu>
          {/* Status remains available to screen-reader users when its icon is hidden. */}
          {statusLabel && <span className="sr-only">{statusLabel}</span>}
        </div>

        <div className={styles.actions}>
          {/* Continue in… button (non-review) stays in the header at all widths — collapsed to an
              icon-only invisible button at narrow. View pull request button (code review) also stays
              at all widths, collapsed to an icon-only invisible button at narrow. */}
          {showVerbosityControl && <TimelineVerbosityControl />}
          <TaskDebugLogButton taskId={task?.id ?? mostRecentSession?.task_id} sessionId={mostRecentSession?.id} />
          {isCodeReviewMode ? (
            pull?.url ? (
              <PullRequestButton
                pullUrl={pull.url}
                taskId={task?.id}
                repoNwo={pull.repository_nwo}
                collapsed={isNarrow}
              />
            ) : null
          ) : (
            effectiveBranchName && (
              <OpenInMenuV2
                branchName={effectiveBranchName}
                pull={pull}
                taskId={task?.id}
                isCurrentUserTaskCreator={currentUserIsOwner}
                collapsed={isNarrow}
                menuOnly
              />
            )
          )}

          {/* Share button — hidden at narrow, shown in title menu instead */}
          {!isNarrow && showShareButton && (
            <Button
              ref={shareButtonRef}
              variant="default"
              leadingVisual={isShared ? UnlockIcon : LockIcon}
              onClick={handleShareClick}
              data-testid="sticky-header-share-button"
            >
              {isShared ? 'Shared' : 'Share'}
            </Button>
          )}
          <AgentsWorkbenchToggle />
        </div>
      </div>
    </div>
  )
}

// Compact-layout threshold in px, mirroring the former 48rem media query.
// Behavior is unchanged on the full-page task route where the header spans the
// viewport, but the switch is driven by the header's own measured width so it is
// also correct when TaskChat is embedded in a narrower resizable panel.
const NARROW_MAX_WIDTH_PX = 768
// Below this the inline model + session-count row would push the header past
// ~320px, so it is swapped for a compact info button. Kept container-relative
// for the same embedded-panel reason as NARROW_MAX_WIDTH_PX.
const VERY_NARROW_MAX_WIDTH_PX = 480

function matchesViewportWidth(maxWidthPx: number): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches
}

interface ContainerWidthState {
  isNarrow: boolean
  isVeryNarrow: boolean
}

// Seed the compact-layout state from the viewport (SSR-safe, correct on the
// full-page route where viewport and header width agree). Passed to useState by
// reference so it only runs on the initial render.
function getInitialWidthState(): ContainerWidthState {
  return {
    isNarrow: matchesViewportWidth(NARROW_MAX_WIDTH_PX),
    isVeryNarrow: matchesViewportWidth(VERY_NARROW_MAX_WIDTH_PX),
  }
}

/**
 * Returns whether the header should use its compact layouts, measured from the
 * header element itself rather than the browser viewport. The state is seeded
 * from the viewport so the first paint is correct on the full-page route (where
 * viewport and header width agree); once mounted, a ResizeObserver on the
 * returned ref drives the values from the header's real box width.
 */
function useContainerIsNarrow(): [ContainerWidthState, (node: HTMLDivElement | null) => void] {
  const [state, setState] = useState<ContainerWidthState>(getInitialWidthState)
  const measureRef = useCallback((node: HTMLDivElement | null) => {
    if (!node || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width
      if (width === undefined) return
      const next = {isNarrow: width < NARROW_MAX_WIDTH_PX, isVeryNarrow: width < VERY_NARROW_MAX_WIDTH_PX}
      setState(prev => (prev.isNarrow === next.isNarrow && prev.isVeryNarrow === next.isVeryNarrow ? prev : next))
    })
    observer.observe(node)
    // React 19 callback-ref cleanup: disconnect when the ref detaches/unmounts.
    return () => observer.disconnect()
  }, [])
  return [state, measureRef]
}

const MAX_NAME_LENGTH = 100

interface RenameTaskDialogProps {
  currentName: string
  onCancel: () => void
  onSubmit: (newName: string) => Promise<void> | void
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
}

function RenameTaskDialog({currentName, onCancel, onSubmit, returnFocusRef}: RenameTaskDialogProps) {
  const [name, setName] = useState(currentName)
  const [isPending, setIsPending] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const errorId = useId()

  const trimmed = name.trim()
  const isEmpty = trimmed.length === 0
  const isTooLong = trimmed.length > MAX_NAME_LENGTH
  const validationError = isEmpty
    ? 'Name is required'
    : isTooLong
      ? `Name must be ${MAX_NAME_LENGTH} characters or fewer`
      : null
  const shownError = validationError ?? submitError

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (validationError || isPending) return
    if (trimmed === currentName) {
      onCancel()
      return
    }
    setSubmitError(null)
    setIsPending(true)
    try {
      await onSubmit(trimmed)
      // On success the parent unmounts this dialog, so no further state updates are needed.
    } catch {
      const message = 'Something went wrong renaming the task. Please try again.'
      setSubmitError(message)
      setIsPending(false)
      // FormControl.Validation is not a live region, so announce the async
      // failure explicitly so screen reader users learn the rename failed
      // (WCAG 4.1.3). Validation errors are announced by the field on submit, so
      // only the async failure is announced here to avoid double-speaking.
      announce(message, {assertive: true})
    }
  }

  return (
    <Dialog
      title="Rename task"
      onClose={onCancel}
      width="medium"
      returnFocusRef={returnFocusRef}
      initialFocusRef={inputRef}
    >
      <Dialog.Body>
        <form onSubmit={handleSubmit}>
          <FormControl required>
            <FormControl.Label>Name</FormControl.Label>
            <FormControl.Caption>Up to {MAX_NAME_LENGTH} characters.</FormControl.Caption>
            <TextInput
              ref={inputRef}
              value={name}
              onChange={e => {
                setName(e.target.value)
                setSubmitError(null)
              }}
              onKeyDown={e => {
                // Enter confirms an active IME composition (Japanese/Chinese/Korean);
                // don't let it submit a partial name.
                if (!e.nativeEvent.isComposing) return
                // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                if (e.key === 'Enter') e.preventDefault()
              }}
              aria-describedby={shownError ? errorId : undefined}
              validationStatus={shownError ? 'error' : undefined}
              block
            />
            {shownError && (
              <FormControl.Validation id={errorId} variant="error">
                {shownError}
              </FormControl.Validation>
            )}
          </FormControl>
        </form>
      </Dialog.Body>
      <Dialog.Footer>
        <Dialog.Buttons
          buttons={[
            {type: 'button', onClick: onCancel, content: 'Cancel'},
            {
              type: 'submit',
              onClick: handleSubmit,
              content: 'Update',
              buttonType: 'primary',
              // Only disable for validation errors — not while pending. Disabling
              // a loading button drops it from keyboard focus and can disrupt
              // focus while the dialog is open (Primer loading-button guidance).
              // `handleSubmit` already guards duplicate submits via `isPending`.
              disabled: !!validationError,
              loading: isPending,
            },
          ]}
        />
      </Dialog.Footer>
    </Dialog>
  )
}
